import { useEffect, useRef } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { cn } from '@shared/lib/utils'
import { FieldLabel } from './field-label'
import { SearchableSelect } from './searchable-select'
import { SELECT_TRIGGER_CLASSES } from './form-styles'

interface SelectFieldProps {
  label: string
  required?: boolean
  value: string
  onChange: (value: string) => void
  options: ReadonlyArray<{ value: string; label: string }>
  placeholder?: string
  className?: string
  searchable?: boolean
  uppercase?: boolean
  autoFocus?: boolean
  disabled?: boolean
}

export function SelectField({
  label,
  required,
  value,
  onChange,
  options,
  placeholder,
  className,
  searchable = true,
  uppercase = true,
  autoFocus,
  disabled,
}: SelectFieldProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!searchable && autoFocus && triggerRef.current) {
      triggerRef.current.focus()
    }
  }, [autoFocus, searchable])

  const triggerClassName = cn(
    SELECT_TRIGGER_CLASSES,
    uppercase && '[&>span:not([data-placeholder])]:uppercase',
    className
  )

  return (
    <div className="space-y-2">
      <FieldLabel required={required}>{label}</FieldLabel>
      {searchable ? (
        <SearchableSelect
          ref={triggerRef}
          value={value}
          onChange={onChange}
          options={options}
          placeholder={placeholder}
          uppercase={uppercase}
          className={triggerClassName}
          autoFocus={autoFocus}
          disabled={disabled}
        />
      ) : (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger ref={triggerRef} className={triggerClassName}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className={uppercase ? 'uppercase' : ''}
              >
                {uppercase ? option.label.toUpperCase() : option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}
